<script>
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Parallax from '$lib/Components/Parallax/Parallax.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import AverageSaved from '$lib/PageSections/Charts/AverageSaved/AverageSaved.svelte';
	import CommercialForm from '$lib/PageSections/CommercialForm/CommercialForm.svelte';
	import NoiseReductionComparison from '$lib/PageSections/DataDrivenResults/NoiseReductionComparison.svelte';
	import FeaturedArticles from '$lib/PageSections/FeaturedArticles/FeaturedArticles.svelte';

	import parallaxImage from './images/commercial-soundproof-hotel-bottom.png';
	import hotel from './images/commonwealth-hotel-05.jpeg';
	import logos from './images/hotel-logos.jpg';
</script>

<Section backgroundColor={'white'}>
	<p>
		Hotel guests want quiet rooms. If outside noise is disturbing their sleep,
		you can expect an increase in negative reviews. Large hotel windows offer
		plenty of natural lighting, but they also let noise in.
	</p>
	<p>
		There's a noise solution that works, and it doesn't require down time
		leaving rooms unoccupied. Acoustic Commercial Grade Indow inserts are an
		acrylic window insert designed to greatly reduce noise and drafts and can
		withstand commercial grade cleaners.
	</p>
	<Image
		src={hotel}
		alt="A lit-up hotel sign overlooking the street at dusk."
	/>

	<p>
		Acoustic Commercial inserts are an extra layer against sound vibrations. The
		deeper the window frame, the bigger the air pocket between insert and
		window—the better the performance.
	</p>
	<p>
		Our patented silicone compression tube on each insert provides a
		draft-blocking seal to keep your space comfortable.
	</p>
</Section>

<Section backgroundColor={'white'}>
	<h2>How Much Noise Reduction Can I Expect?</h2>
	<p>
		A lot. Commercial Acoustic Grade inserts reduce noise from its current level
		by up to 50% when installed in double-pane windows. When paired with
		single-pane windows, inserts can reduce outside noise by up to 70%.
	</p>
	<p>
		We want to ensure our inserts address your noise problem. We will discuss
		with you the source of the noise, its distance from your space and features
		of your windows. Read our full Noise Reduction Sheet to learn about how
		noise infiltrates a space, and how Indow inserts stop it.
	</p>
</Section>
<NoiseReductionComparison titleString={null} />
<Section backgroundColor="white">
	<h2>Hotel Case Studies</h2>
	<p>
		Our inserts block noise in bed and breakfasts, boutique inns and large hotel
		chains. Here are some of our clients, made happy by a decrease in noise and
		an increase in guest comfort:
	</p>
	<Image src={logos} alt="Logos of the clients we have had in the past." />
	<a class="text-xl" href="/resources/case-studies">Our Case Study Portfolio</a>
</Section>

<Section backgroundColor="white">
	<h2>Indow Inserts Also Save Money on Energy Bills</h2>
	<p>
		A
		<a
			href="https://www.pnnl.gov/main/publications/external/technical_reports/PNNL-22855.pdf"
			target="_blank"
			rel="external"
		>
			U.S. Department of Energy study
		</a>
		found that Indow inserts reduced HVAC usage in a Seattle home by 20%. Portland
		State University's Green Building Research Lab found similar results.
	</p>
	<p>
		You'll begin saving money immediately on your hotel's energy bills,
		increasing energy efficiency and putting more money back into the budget.
	</p>
</Section>
<AverageSaved title={null} />
<Section backgroundColor="white">
	<h2>How Much Do Negative Reviews Cost?</h2>
	<p>
		The longer you leave your noise issues unaddressed, the more it will cost
		you. The national average on the effects of a negative review show that a
		single review can lead to the loss of 30 potential customers.
	</p>
	<p>
		Four negative reviews will negatively impact your star rating. Our estimates
		show that a decrease in star rating can lead to a 30% loss in revenue.
	</p>
	<p>
		Don't delay addressing your noise problem because you can't afford to leave
		your rooms vacant during a project.
	</p>
</Section>

<Section backgroundColor="white">
	<h2>No Construction: Rooms Stay in Service</h2>
	<p>
		Window replacement projects are invasive and time-consuming. You are forced
		to close rooms during construction and lose money due to unfilled rooms.
		Indow inserts press into the interior of existing window frames in a matter
		of minutes – no painful, costly transition period.
	</p>
	<p>
		Each Indow insert is held snugly in place by our patented silicone
		Compression Tube. It allows you to avoid damaging tracks or magnetic
		systems, and it leaves no trace when removed.
	</p>
	<Youtube src="https://www.youtube.com/embed/8P4Q-Bu5YwQ" />
</Section>

<Section backgroundColor={'white'}>
	<h2>What is the Ordering Process?</h2>
	<Bubble>
		<ul>
			<li>Schedule your free consultation & get a free estimate.</li>
			<li>Place Your Order.</li>
			<li>Receive our Laser Measuring Kit & measure your windows.</li>
			<li>Receive your inserts when completed.</li>
		</ul>
	</Bubble>
	<p class="footnote">
		Lead times may vary based on our current production queue and the size of
		your order.
	</p>
</Section>
<CommercialForm focus={{ sound: true }} />
<Parallax image={parallaxImage} />
<Awards />
<FeaturedArticles />
